import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandMark from "@/components/BrandMark";
import { SpinnerGap, ShieldCheck, Key } from "@phosphor-icons/react";

const inputCls =
  "mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-shadow";
const labelCls = "text-xs uppercase tracking-[0.2em] font-semibold text-slate-500";
const buttonCls =
  "w-full flex items-center justify-center gap-2 rounded-md bg-slate-900 text-white py-2.5 text-sm font-medium hover:bg-slate-800 active:scale-[0.99] transition-all duration-200 disabled:opacity-60";

/**
 * Accepting an administrator's invitation (FR-23).
 *
 * Replaces self-registration: the account already exists and this only sets
 * its first password, so an administrator never handles someone else's
 * credential. Enrolling a second factor follows immediately (FR-24).
 */
export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [stage, setStage] = useState("password"); // password | enroll | codes
  const [interimToken, setInterimToken] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const interimHeaders = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
  const fail = (err) =>
    toast.error(formatApiError(err.response?.data?.detail) || err.message);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invitation", { token, password });
      if (!data.mfa_enrollment_required) {
        toast.success("Account ready. Please sign in.");
        navigate("/login");
        return;
      }
      setInterimToken(data.enrollment_token);
      const { data: enrol } = await api.post("/auth/mfa/enroll", {},
        interimHeaders(data.enrollment_token));
      setEnrollment(enrol);
      setStage("enroll");
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  };

  const submitEnrollment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/enroll/verify", { code },
        interimHeaders(interimToken));
      setRecoveryCodes(data.recovery_codes || []);
      setStage("codes");
    } catch (err) {
      setCode("");
      fail(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-8">
      <div className="w-full max-w-sm bg-white rounded-lg border border-slate-200 p-8">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-md bg-slate-900 flex items-center justify-center">
            <BrandMark size={22} className="text-white" />
          </div>
          <span className="font-display font-semibold text-xl text-slate-900">BioLIMS</span>
        </div>

        {stage === "password" && (
          <>
            <h1 className="text-2xl font-display font-semibold tracking-tight text-slate-900">
              Activate your account
            </h1>
            <p className="text-slate-500 mt-2 mb-6 text-sm">
              Choose a password. It must be at least 12 characters and mix upper and
              lower case, a digit and a symbol.
            </p>
            <form onSubmit={submitPassword} className="space-y-4">
              {!params.get("token") && (
                <div>
                  <label className={labelCls} htmlFor="invite-token">Invitation code</label>
                  <input data-testid="invite-token" id="invite-token" value={token} required
                    onChange={(e) => setToken(e.target.value)}
                    className={`${inputCls} font-mono text-xs`} />
                </div>
              )}
              <div>
                <label className={labelCls} htmlFor="invite-password">Password</label>
                <input data-testid="invite-password" id="invite-password" type="password" value={password}
                  required onChange={(e) => setPassword(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="invite-confirm">Confirm password</label>
                <input data-testid="invite-confirm" id="invite-confirm" type="password" value={confirm}
                  required onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
              </div>
              <button data-testid="invite-submit" disabled={loading} className={buttonCls}>
                {loading && <SpinnerGap size={18} className="animate-spin" />}
                Continue
              </button>
            </form>
          </>
        )}

        {stage === "enroll" && (
          <>
            <div className="flex items-center gap-2 text-slate-900">
              <ShieldCheck size={22} weight="duotone" />
              <h1 className="text-xl font-display font-semibold tracking-tight">
                Set up two-factor authentication
              </h1>
            </div>
            <p className="text-slate-500 mt-2 mb-5 text-sm">
              Scan this code with an authenticator app, then enter the six digits it
              shows.
            </p>
            {enrollment?.qr_svg && (
              <div className="w-44 h-44 mx-auto mb-4 [&>svg]:w-full [&>svg]:h-full"
                dangerouslySetInnerHTML={{ __html: enrollment.qr_svg }} />
            )}
            <details className="mb-5">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-900">
                Can't scan? Enter the key manually
              </summary>
              <code className="mt-2 block break-all rounded-md bg-slate-50 border border-slate-200 p-2 text-xs font-mono text-slate-700">
                {enrollment?.secret}
              </code>
            </details>
            <form onSubmit={submitEnrollment} className="space-y-4">
              <div>
                <label className={labelCls} htmlFor="invite-mfa-code">Verification code</label>
                <input id="invite-mfa-code" value={code} required autoComplete="one-time-code"
                  onChange={(e) => setCode(e.target.value)}
                  className={`${inputCls} font-mono tracking-[0.3em]`} placeholder="000000" />
              </div>
              <button disabled={loading} className={buttonCls}>
                {loading && <SpinnerGap size={18} className="animate-spin" />}
                Activate
              </button>
            </form>
          </>
        )}

        {stage === "codes" && (
          <>
            <div className="flex items-center gap-2 text-slate-900">
              <Key size={22} weight="duotone" />
              <h1 className="text-xl font-display font-semibold tracking-tight">
                Save your recovery codes
              </h1>
            </div>
            <p className="text-slate-500 mt-2 mb-5 text-sm">
              Each code signs you in once if you lose your authenticator. They are shown
              now and never again.
            </p>
            <div className="grid grid-cols-2 gap-1.5 rounded-md border border-slate-200 bg-slate-50 p-3 font-mono text-xs text-slate-700">
              {recoveryCodes.map((c) => <span key={c}>{c}</span>)}
            </div>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(recoveryCodes.join("\n"));
                toast.success("Copied to clipboard");
              }}
              className="mt-3 text-xs text-slate-500 hover:text-slate-900"
            >
              Copy all
            </button>
            <button
              onClick={async () => { await login(); navigate("/"); }}
              className={`${buttonCls} mt-5`}
            >
              I have saved them, continue
            </button>
          </>
        )}
      </div>
    </div>
  );
}
